import ms from 'ms';
import { useCallback, useEffect, useRef } from 'react';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type EngineDataLocation } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/types';
import { getEngine } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/utils/getEngine';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import { MIN_LENGTH } from '../engines/search';
import { cleanInputValue } from '../utils/cleanInputValue';

interface UseLocationSearchProps {
  biasLocationCode?: string;
  locationInputRef: React.RefObject<HTMLInputElement>;
  providerName: 'bing' | 'google';
  setDisplayError: (error: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  updateLocation: (location: EngineDataLocation) => void;
  updateOnline: (online: boolean) => void;
}

interface UseLocationSearchReturn {
  cleanup: () => void;
  initSearch: () => void;
}

export const useLocationSearch = ({
  locationInputRef,
  providerName,
  biasLocationCode,
  updateLocation,
  updateOnline,
  setDisplayError,
  setIsLoading,
}: UseLocationSearchProps): UseLocationSearchReturn => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const { flagUjSearchLocationAnonymous } = useFeatureFlag();
  const timeoutRef = useRef<NodeJS.Timeout | number>();
  const searchInstanceRef = useRef<any>();

  const Engine = getEngine(providerName);

  const initSearch = useCallback(() => {
    const regionCountryCode =
      currentUser?.last_location?.country_code ||
      currentNetwork?.bias_location?.country_code ||
      biasLocationCode;

    try {
      const search = new Engine({
        container: locationInputRef.current,
        // Bing provider do not support region
        region: providerName === 'bing' ? undefined : regionCountryCode,
        onlineEnabled: false,
        onlineLabel: '',
        anonymousLocations: flagUjSearchLocationAnonymous,
      });

      searchInstanceRef.current = search;

      search.$container
        .on('typeahead:select', (_event, data) => {
          updateOnline(false);
          const locationData = search.data(data);

          if (locationData) {
            // Clear any pending timeouts
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = undefined;
            }

            // Update input with selected location text
            if (locationInputRef.current) {
              const selectedText =
                locationData.location ||
                locationData.address ||
                locationData.formatted_address ||
                locationData.name ||
                locationData.description ||
                '';

              const finalText = selectedText || locationInputRef.current.value;

              locationInputRef.current.value = finalText;

              const event = new Event('input', { bubbles: true });

              locationInputRef.current.dispatchEvent(event);
            }

            updateLocation(locationData);

            // Clean input and close dropdown after a short delay
            const timeoutId = setTimeout(() => {
              if (locationInputRef.current) {
                const currentValue = locationInputRef.current.value;
                const cleanedValue = cleanInputValue(currentValue);

                if (currentValue !== cleanedValue) {
                  locationInputRef.current.value = cleanedValue;
                }
              }
              if (
                search.$container &&
                typeof search.$container.typeahead === 'function'
              ) {
                search.$container.typeahead('close');
              }
            }, 10);

            // Store timeout ID for cleanup
            timeoutRef.current = timeoutId;
          }

          setDisplayError(false);
          setIsLoading(false);
        })
        .on('typeahead:asyncrequest', (_, __, query) => {
          if (query.length >= MIN_LENGTH) {
            setIsLoading(true);
          }
        })
        .on('typeahead:render typeahead:active typeahead:open', (event) => {
          const parent = event.target.parentElement;
          const dropdownMenu = parent.querySelector('.dropdown-menu');
          const emptySuggestions = dropdownMenu.classList.contains('tt-empty');

          event.currentTarget.setAttribute(
            'aria-expanded',
            emptySuggestions ? 'false' : 'true',
          );

          if (emptySuggestions) {
            const politeElement = parent.querySelector('[aria-live="polite"]');

            if (politeElement) {
              politeElement.innerHTML = '';
            }
          }
        })
        .on('typeahead:asynccancel typeahead:asyncreceive', () => {
          setIsLoading(false);
        });
    } catch (error) {
      errorReporting.captureException(error);
    }
  }, [
    currentUser?.last_location?.country_code,
    currentNetwork?.bias_location?.country_code,
    biasLocationCode,
    Engine,
    providerName,
    flagUjSearchLocationAnonymous,
    locationInputRef,
    updateOnline,
    updateLocation,
    setDisplayError,
    setIsLoading,
  ]);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  // Initialize search engine when ready
  useEffect(() => {
    let maxAttempts = 20;

    const interval = setInterval(() => {
      if (Engine.isReady()) {
        clearInterval(interval);
        initSearch();
      }

      if (maxAttempts === 0) {
        clearInterval(interval);

        errorReporting.captureException(
          new Error(
            `The search engine ${providerName} is not loaded after ${ms(20 * 200, { long: true })}`,
          ),
        );
      }

      maxAttempts -= 1;
    }, 200);

    return () => clearInterval(interval);
  }, [initSearch, Engine, providerName]);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  return {
    initSearch,
    cleanup,
  };
};
